import { useState, useEffect } from 'react';
import { getSettings, updateSetting, createSetting } from '../../../services/settings';
import { useAuth } from '../../../hooks/useAuth';
import Toast from '../../../components/Common/Toast';
import Modal from '../../../components/Common/Modal';
import { 
  Settings, 
  Save, 
  X, 
  Edit3, 
  Lock, 
  ShieldCheck, 
  Coins, 
  Bell, 
  Info,
  Loader2,
  PlusCircle,
  Tag,
  DollarSign,
  Trash2
} from 'lucide-react';

const SystemSettings = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState([]);
  const [editing, setEditing] = useState(null);
  const [tempValue, setTempValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [toast, setToast] = useState(null);
  
  // New setting modal state
  const [showModal, setShowModal] = useState(false);
  const [newSetting, setNewSetting] = useState({ key: '', value: '', description: '' });
  const [creating, setCreating] = useState(false);

  // Case types management
  const [caseTypes, setCaseTypes] = useState([]);
  const [newType, setNewType] = useState('');
  const [editingAmounts, setEditingAmounts] = useState({});

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const data = await getSettings();
      setSettings(Array.isArray(data) ? data : []);
      
      // Extract case types setting
      const typesSetting = data.find(s => s.key === 'case_types');
      if (typesSetting && typesSetting.value) {
        try {
          const parsed = JSON.parse(typesSetting.value);
          setCaseTypes(Array.isArray(parsed) ? parsed : []);
        } catch (e) {
          setCaseTypes([]);
        }
      } else {
        // Default case types if not set
        setCaseTypes(['welfare', 'education', 'medical', 'funeral']);
      }
    } catch (err) {
      console.error('Failed to load settings', err);
      setToast({ type: 'error', message: 'Failed to load settings' });
    } finally {
      setLoading(false);
    }
  };

  // Helper to get amount for a case type
  const getAmountForType = (type) => {
    const key = `case_type_${type}_amount`;
    const setting = settings.find(s => s.key === key);
    return setting ? setting.value : '';
  };

  const updateCaseTypes = async (newTypes) => {
    try {
      await updateSetting('case_types', JSON.stringify(newTypes));
      setCaseTypes(newTypes);
      setToast({ type: 'success', message: 'Case types updated' });
      loadSettings(); // refresh to get amounts
    } catch (err) {
      setToast({ type: 'error', message: 'Failed to update case types' });
    }
  };

  const addCaseType = async () => {
    if (!newType.trim()) return;
    if (caseTypes.includes(newType.trim())) {
      setToast({ type: 'error', message: 'Type already exists' });
      return;
    }
    const updated = [...caseTypes, newType.trim()];
    await updateCaseTypes(updated);
    setNewType('');
  };

  const removeCaseType = async (type) => {
    const updated = caseTypes.filter(t => t !== type);
    await updateCaseTypes(updated);
  };

  const updateAmount = async (type, amount) => {
    const key = `case_type_${type}_amount`;
    try {
      await updateSetting(key, amount);
      setEditingAmounts(prev => ({ ...prev, [type]: false }));
      setToast({ type: 'success', message: `Amount for ${type} updated` });
      loadSettings(); // refresh amounts
    } catch (err) {
      setToast({ type: 'error', message: 'Failed to update amount' });
    }
  };

  const handleSave = async (key) => {
    setSavingKey(key);
    try {
      await updateSetting(key, tempValue);
      setToast({ type: 'success', message: 'System updated successfully' });
      setEditing(null);
      loadSettings();
    } catch (err) {
      setToast({ type: 'error', message: err.response?.data?.error || 'Update failed' });
    } finally {
      setSavingKey(null);
    }
  };

  const handleCreateSetting = async () => {
    if (!newSetting.key.trim()) {
      setToast({ type: 'error', message: 'Setting key is required' });
      return;
    }
    setCreating(true);
    try {
      await createSetting(newSetting);
      setToast({ type: 'success', message: 'Setting created successfully' });
      setShowModal(false);
      setNewSetting({ key: '', value: '', description: '' });
      loadSettings();
    } catch (err) {
      setToast({ type: 'error', message: err.response?.data?.error || 'Creation failed' });
    } finally {
      setCreating(false);
    }
  };

  const formatKey = (key) => key.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());

  const getIcon = (key) => {
    if (key.includes('FEE') || key.includes('AMOUNT')) return <Coins className="text-emerald-500" size={20} />;
    if (key.includes('NOTIF')) return <Bell className="text-blue-500" size={20} />;
    if (key.includes('SECURITY') || key.includes('AUTH')) return <ShieldCheck className="text-rose-500" size={20} />;
    return <Settings className="text-slate-400" size={20} />;
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
      <Loader2 className="animate-spin text-indigo-600" size={40} />
      <p className="text-slate-500 font-bold animate-pulse uppercase tracking-widest text-xs">Loading configuration...</p>
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto p-6 md:p-10 space-y-8 animate-in fade-in duration-500">
      
      {/* Header Area */}
      <header className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-slate-100 pb-8">
        <div>
          <div className="flex items-center gap-2 text-indigo-600 mb-1">
            <Lock size={14} strokeWidth={3} />
            <span className="text-[10px] font-black uppercase tracking-[0.2em]">Administrative Shell</span>
          </div>
          <h1 className="text-4xl font-black text-slate-900 tracking-tight">System Settings</h1>
          <p className="text-slate-500 font-medium">Configure core business logic and global variables.</p>
        </div>
        <div className="flex gap-3">
          {(user?.role === 'super_admin' || user?.role === 'org_admin') && (
            <button 
              onClick={() => setShowModal(true)}
              className="flex items-center gap-2 bg-indigo-600 text-white px-5 py-3 rounded-2xl font-bold hover:bg-indigo-700 transition-all shadow-md"
            >
              <PlusCircle size={18} />
              Add New Setting
            </button>
          )}
          <div className="bg-amber-50 border border-amber-100 p-3 rounded-xl flex items-center gap-3">
            <Info className="text-amber-500" size={18} />
            <p className="text-[11px] text-amber-700 font-bold leading-tight">
              Changes here affect <br/> all member transactions.
            </p>
          </div>
        </div>
      </header>

      {/* Case Types Management Card */}
      <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-slate-100 bg-gradient-to-r from-indigo-50 to-white">
          <div className="flex items-center gap-2">
            <Tag className="text-indigo-600" size={20} />
            <h2 className="text-xl font-black text-slate-800">Case Types & Amounts</h2>
          </div>
          <p className="text-slate-500 text-sm mt-1">Define the types of welfare cases and their default contribution amounts.</p>
        </div>
        <div className="p-6 space-y-6">
          <div className="flex flex-wrap gap-2 items-center">
            {caseTypes.map((type) => (
              <div key={type} className="inline-flex items-center gap-2 bg-slate-100 rounded-full px-3 py-1.5 text-sm font-medium">
                <span>{type}</span>
                <button
                  onClick={() => removeCaseType(type)}
                  className="text-slate-400 hover:text-rose-500 transition-colors"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
            <div className="inline-flex items-center gap-2">
              <input
                type="text"
                value={newType}
                onChange={e => setNewType(e.target.value)}
                placeholder="New type"
                className="border border-slate-200 rounded-full px-3 py-1.5 text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
              />
              <button
                onClick={addCaseType}
                className="bg-indigo-600 text-white rounded-full px-3 py-1.5 text-sm font-bold hover:bg-indigo-700 transition-colors"
              >
                Add
              </button>
            </div>
          </div>
          <div className="border-t border-slate-100 pt-6">
            <h3 className="text-sm font-black text-slate-500 uppercase tracking-wider mb-4">Default Amounts (KSh)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {caseTypes.map((type) => {
                const amount = getAmountForType(type);
                const isEditing = editingAmounts[type];
                return (
                  <div key={type} className="flex items-center justify-between bg-slate-50 p-3 rounded-xl">
                    <span className="capitalize font-bold text-slate-700">{type}</span>
                    <div className="flex items-center gap-2">
                      {isEditing ? (
                        <>
                          <input
                            type="number"
                            defaultValue={amount}
                            onBlur={(e) => updateAmount(type, e.target.value)}
                            autoFocus
                            className="w-28 px-2 py-1 border border-indigo-300 rounded-lg text-right"
                          />
                          <button onClick={() => setEditingAmounts(prev => ({ ...prev, [type]: false }))} className="text-slate-400 hover:text-slate-600">
                            <X size={16} />
                          </button>
                        </>
                      ) : (
                        <>
                          <span className="font-mono text-slate-600">{amount || 'Not set'}</span>
                          <button onClick={() => setEditingAmounts(prev => ({ ...prev, [type]: true }))} className="text-indigo-600 hover:text-indigo-800">
                            <Edit3 size={16} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Other Settings Grid */}
      <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-slate-100 bg-slate-50">
          <h2 className="text-xl font-black text-slate-800">Other Configuration</h2>
        </div>
        <div className="divide-y divide-slate-100">
          {settings.filter(s => s.key !== 'case_types').length === 0 ? (
            <div className="text-center py-10">
              <p className="text-slate-400 text-sm">No other settings found.</p>
            </div>
          ) : (
            settings.filter(s => s.key !== 'case_types').map(setting => (
              <div key={setting.key} className="p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="p-2 rounded-xl bg-slate-50">
                    {getIcon(setting.key)}
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800">{formatKey(setting.key)}</h3>
                    <p className="text-sm text-slate-400">{setting.description}</p>
                    <code className="text-[10px] text-slate-300">{setting.key}</code>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {editing === setting.key ? (
                    <div className="flex items-center gap-2">
                      <input
                        value={tempValue}
                        onChange={e => setTempValue(e.target.value)}
                        autoFocus
                        className="border border-indigo-300 rounded-xl px-3 py-2 w-48 text-right"
                      />
                      <button onClick={() => handleSave(setting.key)} disabled={savingKey === setting.key} className="bg-slate-900 text-white p-2 rounded-xl">
                        {savingKey === setting.key ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                      </button>
                      <button onClick={() => setEditing(null)} className="bg-slate-100 p-2 rounded-xl">
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <>
                      <span className="font-mono text-slate-700">{setting.value}</span>
                      <button onClick={() => { setEditing(setting.key); setTempValue(setting.value); }} className="text-indigo-600 hover:text-indigo-800">
                        <Edit3 size={16} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Modal for creating a new setting */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Add New Setting">
        <div className="space-y-4 p-2">
          <div className="form-group">
            <label className="block text-sm font-bold text-slate-700 mb-2">Setting Key</label>
            <input
              type="text"
              value={newSetting.key}
              onChange={e => setNewSetting({ ...newSetting, key: e.target.value })}
              placeholder="e.g., registration_fee"
              className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
              required
            />
            <p className="text-xs text-slate-400 mt-1">Use lowercase letters, numbers, and underscores only.</p>
          </div>
          <div className="form-group">
            <label className="block text-sm font-bold text-slate-700 mb-2">Value</label>
            <input
              type="text"
              value={newSetting.value}
              onChange={e => setNewSetting({ ...newSetting, value: e.target.value })}
              placeholder="e.g., 500"
              className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
            />
          </div>
          <div className="form-group">
            <label className="block text-sm font-bold text-slate-700 mb-2">Description</label>
            <textarea
              value={newSetting.description}
              onChange={e => setNewSetting({ ...newSetting, description: e.target.value })}
              placeholder="What does this setting control?"
              rows="3"
              className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
            />
          </div>
          <div className="flex gap-3 pt-4">
            <button
              onClick={handleCreateSetting}
              disabled={creating}
              className="flex-1 bg-indigo-600 text-white py-3 rounded-xl font-bold hover:bg-indigo-700 transition-all disabled:opacity-50"
            >
              {creating ? <Loader2 className="animate-spin mx-auto" size={20} /> : 'Create Setting'}
            </button>
            <button
              onClick={() => setShowModal(false)}
              className="flex-1 bg-slate-100 text-slate-600 py-3 rounded-xl font-bold hover:bg-slate-200 transition-all"
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
};

export default SystemSettings;